import { Controller } from "@hotwired/stimulus";

import { prefersReducedMotion } from "~/lib/viewport";
import { Clock, whileInView } from "~/lib/web/loop";

const STAGGER_MS = 340;

const FADE_MS = 650;

const HOLD_MS = 2600;

const RESET_MS = 450;

const GAP_MS = 600;

export default class WebSubsController extends Controller<HTMLElement> {
  static override targets = ["row"];
  declare readonly rowTargets: HTMLElement[];

  private clock = new Clock();
  private stop: (() => void) | null = null;

  override connect(): void {
    if (!this.rowTargets.length) return;
    if (prefersReducedMotion()) {
      for (const row of this.rowTargets) row.classList.add("is-in");
      return;
    }
    this.stop = whileInView(
      this.element,
      () => this.cycle(),
      () => this.reset(),
    );
  }

  override disconnect(): void {
    this.stop?.();
    this.stop = null;
    this.reset();
  }

  /* Leaving the rows as they are would restart the next cycle mid-fade: the
     rows still on screen keep their opacity while the loop re-staggers from the
     first one, so they blink out of step with each other instead of as a list. */
  private reset(): void {
    this.clock.clear();
    this.element.classList.remove("is-resetting");
    for (const row of this.rowTargets) row.classList.remove("is-in");
  }

  private cycle(): void {
    const rows = this.rowTargets;
    this.element.classList.remove("is-resetting");
    rows.forEach((row, index) => {
      this.clock.at(index * STAGGER_MS, () => row.classList.add("is-in"));
    });

    // The last row starts at (n-1) x stagger and needs its own fade to finish
    // before the hold begins.
    const settled = (rows.length - 1) * STAGGER_MS + FADE_MS;
    this.clock.at(settled + HOLD_MS, () => {
      this.element.classList.add("is-resetting");
      for (const row of rows) row.classList.remove("is-in");
    });
    this.clock.at(settled + HOLD_MS + RESET_MS + GAP_MS, () => this.cycle());
  }
}
